import React from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpen, Library } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CoursesModuleConfig, ModuleFieldComponentProps } from '@/types/space-wizard'

/**
 * Courses' only space-level question: a list of courses, or a space that is one course.
 * "List" comes first and is the default (PADROES-UI §9); absent in the stored config means list.
 */
export const CoursesModuleFields: React.FC<
  ModuleFieldComponentProps<CoursesModuleConfig> & { variant?: 'wizard' | 'settings' }
> = ({ config, onChange, disabled = false, variant = 'wizard' }) => {
  const { t } = useTranslation(['common'])
  const mode = config.mode === 'single' ? 'single' : 'multi'

  const options = [
    { value: 'multi' as const, icon: Library, title: t('modules.settings.courses.modeMulti'), description: t('modules.settings.courses.modeMultiHint') },
    { value: 'single' as const, icon: BookOpen, title: t('modules.settings.courses.modeSingle'), description: t('modules.settings.courses.modeSingleHint') },
  ]

  return (
    <div className="space-y-2" data-testid="courses-mode">
      <span className="text-sm font-medium text-gray-700">{t('modules.settings.courses.mode')}</span>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map(({ value, icon: Icon, title, description }) => {
          const selected = mode === value
          return (
            <button
              key={value}
              type="button"
              aria-pressed={selected}
              data-value={value}
              disabled={disabled}
              onClick={() => onChange({ ...config, mode: value })}
              className={cn(
                'flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-all',
                'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1',
                selected ? 'border-primary bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', selected ? 'text-primary' : 'text-gray-400')} />
              <span className="min-w-0">
                <span className={cn('block text-sm font-medium', selected ? 'text-primary-700' : 'text-gray-900')}>{title}</span>
                <span className="mt-0.5 block text-xs leading-snug text-gray-500">{description}</span>
              </span>
            </button>
          )
        })}
      </div>
      {mode === 'single' && (
        <p className="text-xs text-gray-500">
          {variant === 'wizard' ? t('modules.settings.courses.hintSingleWizard') : t('modules.settings.courses.hintSingleSettings')}
        </p>
      )}
    </div>
  )
}
